import { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
// 直接具体路径，不走 contexts barrel，否则会把 FloatingIconsContext 等全部
// 反向拖回 hooks/useQueuedPopup，构成长链循环依赖
import { usePopupQueue } from "../contexts/PopupQueueContext";
import { popupTriggerManager } from "../utils/popupTriggers";
import { ROUTES } from "../types/routes";

const POPUP_STORAGE_KEY = "shown_popup_records";

// 标记是否已经执行过清理（每个会话只清理一次）
let hasCleanedThisSession = false;

// 标记是否已经清理过被拒绝状态（页面刷新后清理一次）
let hasCleanedRejectedStatus = false;
// 标记是否已经清理过队列占位状态（页面刷新后清理一次）
let hasCleanedQueuedStatus = false;

// 时间粒度类型
type TimeGranularity = "hour" | "day";

type PopupRecord = {
  time: string;
  timestamp: number;
  wasShown?: boolean;
  wasRejected?: boolean;
  isQueued?: boolean;
};

/**
 * 获取时间字符串（根据粒度）
 * @param granularity - 时间粒度：'hour' 或 'day'
 */
const getTimeString = (granularity: TimeGranularity = "day"): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const date = String(now.getDate()).padStart(2, "0");

  if (granularity === "hour") {
    const hour = String(now.getHours()).padStart(2, "0");
    return `${year}-${month}-${date}-${hour}`;
  }

  return `${year}-${month}-${date}`;
};

/**
 * 获取弹窗记录（包含时间戳、显示状态和拒绝状态）
 */
const getPopupRecords = (): Record<string, PopupRecord> => {
  try {
    const stored = localStorage.getItem(POPUP_STORAGE_KEY);
    if (!stored) {
      return {};
    }

    const records = JSON.parse(stored);

    // 兼容旧格式（string -> object）
    const newRecords: Record<string, PopupRecord> = {};
    Object.keys(records).forEach(key => {
      if (typeof records[key] === "string") {
        // 旧格式：转换为新格式
        newRecords[key] = {
          time: records[key],
          timestamp: Date.now(),
          wasShown: true,
        };
      } else {
        newRecords[key] = records[key];
      }
    });

    return newRecords;
  } catch (error) {
    console.error("[PopupQueue-GetRecords] 解析失败:", error);
    return {};
  }
};

/**
 * 保存弹窗记录
 */
const savePopupRecords = (records: Record<string, PopupRecord>): void => {
  try {
    localStorage.setItem(POPUP_STORAGE_KEY, JSON.stringify(records));
  } catch (error) {
    console.error("[PopupQueue-SaveRecords] 保存弹窗记录失败:", error);
  }
};

/**
 * 清理过期记录
 * - 超过7天的记录全部清理
 * - wasShown: false 且超过1小时的记录清理（允许下次会话重试）
 * - 每个会话只执行一次
 */
const cleanExpiredRecords = (): void => {
  // 每个会话只清理一次
  if (hasCleanedThisSession) {
    return;
  }

  try {
    const records = getPopupRecords();
    const now = Date.now();
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
    const oneHourInMs = 60 * 60 * 1000;

    let hasExpired = false;
    Object.keys(records).forEach(key => {
      const record = records[key];
      // 清理超过7天的记录
      if (now - record.timestamp > sevenDaysInMs) {
        delete records[key];
        hasExpired = true;
      }
      // 清理 wasShown: false 且超过1小时的记录（本次会话已结束，下次允许重试）
      else if (
        record.wasShown === false &&
        now - record.timestamp > oneHourInMs
      ) {
        delete records[key];
        hasExpired = true;
      }
    });

    if (hasExpired) {
      savePopupRecords(records);
    }

    // 标记已清理
    hasCleanedThisSession = true;
  } catch (error) {
    console.error("[PopupQueue-CleanExpired] 清理过期记录失败:", error);
  }
};

/**
 * 检查是否在指定时间粒度内已真正显示过
 * @param popupId - 弹窗ID
 * @param userId - 用户ID（可选）
 * @param granularity - 时间粒度：'hour' 或 'day'（默认为 'day'）
 */
const hasShownInPeriod = (
  popupId: string,
  userId?: string,
  granularity: TimeGranularity = "day"
): boolean => {
  const records = getPopupRecords();
  const storageKey = userId ? `${popupId}_${userId}` : popupId;
  const record = records[storageKey];

  if (!record) {
    return false;
  }

  const currentTime = getTimeString(granularity);

  // 仅当“真正显示过”才阻止自动弹出
  const result = record.time === currentTime && record.wasShown === true;

  return result;
};

/**
 * 检查是否在指定时间粒度内已有“待显示队列占位”记录
 * 仅用于防止同一弹窗在未显示前被重复加入队列
 */
const hasQueuedInPeriod = (
  popupId: string,
  userId?: string,
  granularity: TimeGranularity = "day"
): boolean => {
  const records = getPopupRecords();
  const storageKey = userId ? `${popupId}_${userId}` : popupId;
  const record = records[storageKey];

  if (!record) {
    return false;
  }

  const currentTime = getTimeString(granularity);
  return (
    record.time === currentTime &&
    record.isQueued === true &&
    record.wasShown !== true
  );
};

/**
 * 标记在指定时间粒度内已显示
 * @param popupId - 弹窗ID
 * @param userId - 用户ID（可选）
 * @param granularity - 时间粒度：'hour' 或 'day'（默认为 'day'）
 * @param wasShown - 是否真正显示过（默认 true）
 * @param isQueued - 是否仅作为队列占位（默认 false）
 */
const markShownInPeriod = (
  popupId: string,
  userId?: string,
  granularity: TimeGranularity = "day",
  wasShown: boolean = true,
  isQueued: boolean = false
): void => {
  // 每次标记时都清理一次过期记录
  cleanExpiredRecords();

  const records = getPopupRecords();
  const storageKey = userId ? `${popupId}_${userId}` : popupId;

  // 保留现有记录的其他字段（如 wasRejected）
  const existingRecord = records[storageKey];

  records[storageKey] = {
    ...existingRecord,
    time: getTimeString(granularity),
    timestamp: Date.now(),
    wasShown,
    isQueued,
  };

  savePopupRecords(records);
};

/**
 * 清除弹窗的显示记录（允许再次弹出）
 * @param popupId - 弹窗ID
 * @param userId - 用户ID（可选）
 */
export const clearPopupRecord = (popupId: string, userId?: string): void => {
  try {
    const records = getPopupRecords();
    const storageKey = userId ? `${popupId}_${userId}` : popupId;
    if (records[storageKey]) {
      delete records[storageKey];
      savePopupRecords(records);
    }
  } catch (error) {
    console.error("清除弹窗记录失败:", error);
  }
};

/**
 * 标记弹窗为被拒绝状态
 * @param popupId - 弹窗ID
 * @param userId - 用户ID（可选）
 */
const markPopupRejected = (popupId: string, userId?: string): void => {
  try {
    const records = getPopupRecords();
    const storageKey = userId ? `${popupId}_${userId}` : popupId;

    if (records[storageKey]) {
      records[storageKey].wasRejected = true;
    } else {
      records[storageKey] = {
        time: getTimeString("day"),
        timestamp: Date.now(),
        wasShown: false,
        wasRejected: true,
        isQueued: false,
      };
    }

    savePopupRecords(records);
  } catch (error) {
    console.error("[PopupQueue-MarkRejected] 标记弹窗拒绝状态失败:", error);
  }
};

/**
 * 清理所有被拒绝的弹窗状态（页面刷新后调用）
 *
 * 核心逻辑：
 * 1. wasRejected: true 表示用户主动拒绝了这个弹窗（点击关闭/取消）
 * 2. 页面刷新时，清理 wasRejected 标记，让被拒绝的弹窗可以重新显示
 * 3. 这样用户刷新页面后，之前拒绝的弹窗会再次尝试显示
 * 4. 将 wasShown 改为 false，time 保持不变，这样：
 *    - hasShownInPeriod() 仍会返回 true（因为 time 匹配）
 *    - 但清理后 wasRejected 为 false，所以不会被判定为"拒绝状态"
 *    - 实际上是删除整个记录，让弹窗可以重新尝试
 */
const cleanRejectedStatus = (): void => {
  if (hasCleanedRejectedStatus) {
    return;
  }

  try {
    const records = getPopupRecords();
    let hasChanges = false;

    Object.keys(records).forEach(key => {
      const record = records[key];
      if (record.wasRejected === true) {
        // 直接删除记录，允许重新显示
        delete records[key];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      savePopupRecords(records);
    }

    hasCleanedRejectedStatus = true;
  } catch (error) {
    console.error("[PopupQueue-CleanRejected] 清理被拒绝弹窗状态失败:", error);
  }
};

/**
 * 清理“仅入队占位但未真实展示”的状态（页面刷新后调用）
 *
 * 场景：
 * - 弹窗入队后，可能因为条件变化/队列淘汰未真正展示
 * - 这时会残留 isQueued: true, wasShown: false
 * - 刷新后应允许重新自动评估并弹出，所以清理该占位记录
 */
const cleanQueuedStatus = (): void => {
  if (hasCleanedQueuedStatus) {
    return;
  }

  try {
    const records = getPopupRecords();
    let hasChanges = false;

    Object.keys(records).forEach(key => {
      const record = records[key];
      if (record.isQueued === true && record.wasShown !== true) {
        delete records[key];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      savePopupRecords(records);
    }

    hasCleanedQueuedStatus = true;
  } catch (error) {
    console.error("[PopupQueue-CleanQueued] 清理队列占位状态失败:", error);
  }
};

export interface QueuedPopupConfig {
  // 唯一标识符（用于防止重复显示）
  popupId: string;
  // 优先级（数字越小优先级越高）
  priority: number;
  // 是否应该显示弹窗的条件；trigger 内会传 { isManualTrigger: true }，其它处不传
  shouldShow: (context?: { isManualTrigger?: boolean }) => boolean;
  // 渲染函数（接收 onComplete 回调和可选 context，手动打开时 context.isManualTrigger 为 true）
  renderContent: (
    onComplete: () => void,
    context?: { isManualTrigger?: boolean }
  ) => React.ReactNode;
  // 完成时的回调（可选）
  onComplete?: () => void;
  // 是否限制只显示一次（默认 false）- 根据 showOnceGranularity 限制显示频率
  showOnce?: boolean;
  // 限制显示频率的时间粒度（默认 'day'）
  // 'hour': 一小时内只自动弹出一次
  // 'day': 一天内只自动弹出一次（跨自然日）
  showOnceGranularity?: TimeGranularity;
  // 是否允许手动触发（默认 false）
  allowManualTrigger?: boolean;
  // 是否自动触发（默认 true）
  autoTrigger?: boolean;
  // shouldShow 依赖的值（当这些值变化时重新检查条件）
  dependencies?: any[];
  // 允许显示的页面路径（可选，为空则不限制）
  allowedPaths?: string[];
  // 禁止显示的页面路径（可选，为空则不限制）
  blockedPaths?: string[];
  // 是否需要用户绑定（默认 false）
  // true: 需要用户绑定，每个用户按 showOnceGranularity 显示一次
  // false: 只看设备，不考虑用户，每个设备按 showOnceGranularity 显示一次
  userBinding?: boolean;
  // 获取当前用户ID的函数（当 userBinding 为 true 时必须提供）
  getUserId?: () => string | undefined;
  // 弹窗触发时机类型（用于限制ACTIVITY_QUALIFICATION类型弹窗数量）
  timingType?: string;
  /**
   * 为 true 时：不满足「首页」时也允许先入队占位，等资源到齐后由 PopupQueue 按 allowedPaths 再实际展示。
   * 到账条等非首页即可完成 config 下发的场景可与「队列内 popupId 去重」配合，避免回首页时再抢跑 render。
   */
  allowEnqueueWhenNotHome?: boolean;
}

/**
 * 通用的队列弹窗 Hook
 * 自动处理弹窗的加入队列、显示控制、重复检测等逻辑
 */
export const useQueuedPopup = (config: QueuedPopupConfig) => {
  const {
    popupId,
    priority,
    shouldShow,
    renderContent,
    onComplete,
    showOnce = false,
    showOnceGranularity = "day",
    allowManualTrigger = false,
    autoTrigger = true,
    dependencies = [],
    allowedPaths,
    blockedPaths,
    userBinding = false,
    getUserId,
    timingType,
    allowEnqueueWhenNotHome = false,
  } = config;

  // 页面刷新后清理所有被拒绝状态（只执行一次）
  cleanRejectedStatus();
  // 页面刷新后清理仅占位记录（只执行一次）
  cleanQueuedStatus();

  const {
    enqueue,
    completeCurrentAndNext,
    dequeue,
    suspendCurrent,
    isPaused,
    currentPopup,
  } = usePopupQueue();
  const { pathname } = useLocation();
  /** 仅「从非首页 → 首页」时递增，用于回到首页后再尝试入队（不把 pathname 绑进 effect，避免任意路由抖动全量抢队列） */
  const prevWasHomeRef = useRef<boolean | null>(null);
  const [homeEnterSignal, setHomeEnterSignal] = useState(0);
  const popupIdRef = useRef<string | null>(null);
  const [popupQueueId, setPopupQueueId] = useState<string | null>(null);
  const [isManuallyTriggered, setIsManuallyTriggered] = useState(false);
  const isInQueue = popupQueueId !== null;
  // 追踪本次尝试加入队列但被拒绝的状态（不持久化到 sessionStorage）
  const wasRejectedRef = useRef(false);

  // 获取用户ID（如果需要用户绑定）
  const getCurrentUserId = useCallback((): string | undefined => {
    if (!userBinding) {
      return undefined;
    }
    return getUserId?.();
  }, [userBinding, getUserId]);

  // 检查当前路径是否在首页（用于自动弹出）
  const isHomePage = useCallback((): boolean => {
    const currentPath = window.location.pathname;
    return (
      currentPath === ROUTES.HOME ||
      currentPath === ROUTES.HOME_ALT ||
      currentPath === "/"
    );
  }, []);

  // 添加到队列（自动弹出，仅在首页）
  const addToQueue = useCallback(() => {
    // 如果已经在队列中，跳过
    if (popupIdRef.current) {
      return;
    }

    // 如果本次已经尝试过但被拒绝，不再重复尝试
    if (wasRejectedRef.current) {
      return;
    }

    // 检查业务条件
    const shouldShowResult = shouldShow();
    if (!shouldShowResult) {
      return;
    }

    // 默认仅首页入队；到账条等可先在非首页占位，回首页后由队列按路径展示
    if (!allowEnqueueWhenNotHome) {
      const isHome = isHomePage();
      if (!isHome) {
        return;
      }
    }

    // 获取当前用户ID（如果需要用户绑定）
    const userId = getCurrentUserId();

    // 如果限制只显示一次且在时间粒度内已显示过，跳过
    if (showOnce && hasShownInPeriod(popupId, userId, showOnceGranularity)) {
      return;
    }

    // 如果已有“待显示队列占位”，跳过，避免重复入队
    if (showOnce && hasQueuedInPeriod(popupId, userId, showOnceGranularity)) {
      return;
    }

    // 加入队列
    const enqueuedId = enqueue({
      popupId, // 传入 popupId 用于去重
      priority,
      allowedPaths,
      blockedPaths,
      timingType,
      userBinding,
      getUserId,
      render: () => {
        // render 时再次检查条件
        const stillShouldShow = shouldShow();

        if (!stillShouldShow && !isManuallyTriggered) {
          // 异步清理，避免在 render 中修改状态
          setTimeout(() => {
            if (popupIdRef.current) {
              completeCurrentAndNext();
            }
          }, 0);
          return null;
        }

        // 真正显示时，更新记录为已显示
        if (showOnce) {
          const userId = getCurrentUserId();
          markShownInPeriod(popupId, userId, showOnceGranularity, true, false);
        }

        return renderContent(completeCurrentAndNext, {
          isManualTrigger: isManuallyTriggered,
        });
      },
      onComplete: () => {
        popupIdRef.current = null;
        setPopupQueueId(null);
        setIsManuallyTriggered(false);
        onComplete?.();
      },
      onRejected: () => {
        wasRejectedRef.current = true;
        popupIdRef.current = null; // 重要：被拒绝时也要清空 ref
        setPopupQueueId(null);

        const userId = getUserId ? getUserId() : undefined;

        // 先标记拒绝状态到 sessionStorage
        markPopupRejected(popupId, userId);

        // 然后标记为已显示（markShownInPeriod 会保留 wasRejected）
        if (showOnce) {
          markShownInPeriod(popupId, userId, showOnceGranularity, true, false);
        }
      },
    });

    // 只有成功加入队列（返回非 null）才更新 ref 和标记到 sessionStorage
    if (enqueuedId) {
      popupIdRef.current = enqueuedId;
      setPopupQueueId(enqueuedId);
      if (showOnce) {
        markShownInPeriod(popupId, userId, showOnceGranularity, false, true);
      }
    }
  }, [
    popupId,
    priority,
    shouldShow,
    renderContent,
    enqueue,
    completeCurrentAndNext,
    onComplete,
    showOnce,
    showOnceGranularity,
    isManuallyTriggered,
    isHomePage,
    getCurrentUserId,
    allowedPaths,
    blockedPaths,
    userBinding,
    getUserId,
    timingType,
    allowEnqueueWhenNotHome,
  ]);

  // 手动触发（用于点击事件）
  const trigger = useCallback(() => {
    if (!allowManualTrigger) {
      return;
    }

    setIsManuallyTriggered(true);

    // 如果已经在队列中，不重复添加
    if (popupIdRef.current) {
      const isActuallyCurrentPopup =
        currentPopup?.id === popupIdRef.current ||
        currentPopup?.popupId === popupId;

      // 兜底：如果本地 ref 有值，但当前并不是这个弹窗，认为是僵尸占位，先清理再继续
      if (!isActuallyCurrentPopup) {
        dequeue(popupIdRef.current);
        popupIdRef.current = null;
        setPopupQueueId(null);
      } else {
        return;
      }
    }

    // 再次确认，避免上面清理后仍残留
    if (popupIdRef.current) {
      return;
    }

    // 获取当前用户ID（如果需要用户绑定）
    const userId = getCurrentUserId();

    // 手动触发时，先清除已显示记录，允许重新弹出
    // 这样即使之前被拒绝（wasShown: true），手动点击也能弹出
    if (showOnce) {
      clearPopupRecord(popupId, userId);
    }

    const shouldShowResult = shouldShow({ isManualTrigger: true });

    if (!shouldShowResult) {
      setIsManuallyTriggered(false);
      return;
    }

    // 手动触发时，即使在 blocked 路径中也加入队列
    // 队列系统会在路径合适时自动显示
    const enqueuedId = enqueue({
      popupId, // 传入 popupId 用于去重
      priority,
      allowedPaths,
      blockedPaths,
      timingType,
      userBinding,
      getUserId,
      isManualTrigger: true, // 标记为手动触发
      render: () => {
        // 真正显示时，更新记录为已显示
        if (showOnce) {
          const userId = getCurrentUserId();
          markShownInPeriod(popupId, userId, showOnceGranularity, true, false);
        }
        return renderContent(completeCurrentAndNext, {
          isManualTrigger: true,
        });
      },
      onComplete: () => {
        popupIdRef.current = null;
        setPopupQueueId(null);
        setIsManuallyTriggered(false);
        onComplete?.();
      },
      onRejected: () => {
        popupIdRef.current = null; // 重要：被拒绝时也要清空 ref
        setPopupQueueId(null);
        setIsManuallyTriggered(false);
      },
    });

    // 只有成功加入队列后才标记（标记为未显示）
    if (enqueuedId) {
      popupIdRef.current = enqueuedId;
      setPopupQueueId(enqueuedId);
      if (showOnce) {
        markShownInPeriod(popupId, userId, showOnceGranularity, false, true);
      }
      // 手动触发抢占：当前有其他弹窗在展示时把它挂起（塞回队列按优先级重排），
      // 让手动唤起的弹窗立即展示，而不是排队等待造成"点了没反应"
      suspendCurrent(popupId);
    } else {
      setIsManuallyTriggered(false);
    }
  }, [
    allowManualTrigger,
    shouldShow,
    popupId,
    priority,
    renderContent,
    enqueue,
    completeCurrentAndNext,
    suspendCurrent,
    onComplete,
    allowedPaths,
    blockedPaths,
    showOnce,
    showOnceGranularity,
    getCurrentUserId,
    userBinding,
    getUserId,
    timingType,
    isInQueue,
    popupQueueId,
    currentPopup,
    dequeue,
  ]);

  // 移除队列
  const remove = useCallback(() => {
    if (popupIdRef.current) {
      dequeue(popupIdRef.current);
      popupIdRef.current = null;
      setPopupQueueId(null);
    }
  }, [popupId, dequeue]);

  // 自动注册触发器到全局管理器
  useEffect(() => {
    if (allowManualTrigger) {
      popupTriggerManager.register(popupId, trigger, isInQueue);
      return () => {
        popupTriggerManager.unregister(popupId, trigger);
      };
    }
  }, [popupId, trigger, isInQueue, allowManualTrigger]);

  // 更新队列状态
  useEffect(() => {
    if (allowManualTrigger) {
      popupTriggerManager.updateQueueStatus(popupId, isInQueue, trigger);
    }
  }, [popupId, isInQueue, allowManualTrigger, trigger]);

  useEffect(() => {
    const isHome =
      pathname === ROUTES.HOME ||
      pathname === ROUTES.HOME_ALT ||
      pathname === "/";
    if (prevWasHomeRef.current === null) {
      prevWasHomeRef.current = isHome;
      return;
    }
    const prev = prevWasHomeRef.current;
    prevWasHomeRef.current = isHome;
    if (!prev && isHome) {
      setHomeEnterSignal(n => n + 1);
    }
  }, [pathname]);

  useEffect(() => {
    if (!autoTrigger) {
      // autoTrigger 关闭时，如果已在队列且不是手动触发，则移除
      if (popupIdRef.current && !isManuallyTriggered) {
        remove();
      }
      return;
    }

    // 监听路径变化，如果进入首页则重置被拒绝标记
    const currentPath = window.location.pathname;
    const isHome =
      currentPath === ROUTES.HOME ||
      currentPath === ROUTES.HOME_ALT ||
      currentPath === "/";

    if (isHome) {
      wasRejectedRef.current = false;
    }

    // 如果已在队列且条件不满足，则移除
    // 但在队列暂停期间不移除（保留弹窗，等待恢复后继续）
    if (
      popupIdRef.current &&
      !shouldShow() &&
      !isManuallyTriggered &&
      !isPaused
    ) {
      remove();
      return;
    }

    // 如果不在队列且条件满足，则加入队列（依赖含 currentPopup：上一弹窗关闭后重试，避免入队被拒一次后永不重试）
    // 使用 setTimeout 避免在渲染期间更新状态
    if (!popupIdRef.current) {
      const timer = setTimeout(() => {
        addToQueue();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [
    autoTrigger,
    addToQueue,
    popupId,
    homeEnterSignal,
    currentPopup,
    shouldShow,
    isManuallyTriggered,
    isPaused,
    remove,
    ...dependencies, // 添加用户指定的依赖
  ]);

  return {
    trigger, // 手动触发
    remove, // 移除队列
    isInQueue, // 是否在队列中
  };
};
